<template>
  <div class="mx-auto mw-700">
    <v-spacer class="mt-2" />
    <h3>
      {{ $t('host.realEstateBlacklist.title') }}
    </h3>
    <v-spacer class="mt-4" />

    <v-data-table
      mobile-breakpoint="20"
      :items="hostRealEstateBlacklist"
      :headers="headers"
      :no-data-text="$t('host.realEstateBlacklist.emptyList')"
      :items-per-page="10"
      :footer-props="{
        'items-per-page-text': '',
        'items-per-page-options': [],
      }"
      item-key="id"
      disable-sort
    >
      <template v-slot:[`item.date_created`]="{ item }">
        {{ formatDate(item.date_created) }}
      </template>

      <template v-slot:[`item.notes`]="{ item }">
        <v-tooltip bottom :disabled="!item.notes || item.notes.length <= 40">
          <template v-slot:activator="{ on, attrs }">
            <span
              v-bind="attrs"
              v-on="on"
              class="notes-cell"
            >
              {{ item.notes || '—' }}
            </span>
          </template>
          <span>{{ item.notes }}</span>
        </v-tooltip>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              color="red"
              v-bind="attrs"
              v-on="on"
              @click="openConfirmDialog(item)"
            >
              <v-icon>mdi-undo</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('host.realEstateBlacklist.confirmRemove.tooltip') }}</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <v-dialog v-model="confirmDialog" max-width="500">
      <v-card>
        <v-card-title>
          {{ $t('host.realEstateBlacklist.confirmRemove.title') }}
          <v-spacer />
          <v-btn @click="confirmDialog = false" icon>
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="pt-5 text-body-1">
          {{ $t('host.realEstateBlacklist.confirmRemove.message', { name: itemToRemove ? itemToRemove.real_estate_name : '' }) }}
        </v-card-text>

        <v-divider />

        <v-card-actions class="d-flex justify-end pa-5">
          <v-btn
            elevation="0"
            class="mr-5"
            @click="confirmDialog = false"
            text
          >
            {{ $t('host.realEstateBlacklist.confirmRemove.cancel') }}
          </v-btn>
          <v-btn
            @click="confirmRemove"
            elevation="0"
            color="error"
          >
            {{ $t('host.realEstateBlacklist.confirmRemove.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-spacer class="mt-6" />

    <v-form ref="addForm">
      <v-autocomplete
        v-model="selectedRealEstates"
        :items="realEstateList"
        :label="$t('host.realEstateBlacklist.addForm.realEstateLabel')"
        :loading="isRealEstateSearching"
        :search-input.sync="realEstateSearch"
        item-text="name"
        item-value="id"
        multiple
        chips
        clearable
        hide-no-data
        no-filter
        outlined
      />
      <v-textarea
        v-model="notes"
        :label="$t('host.realEstateBlacklist.addForm.notesLabel')"
        outlined
      />
      <v-btn
        color="red"
        dark
        :disabled="selectedRealEstates.length === 0"
        @click="submitBlacklist"
      >
        <v-icon left>mdi-plus</v-icon>
        {{ $t('host.realEstateBlacklist.addForm.submitButton') }}
      </v-btn>
    </v-form>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { catalogDate } from '@/util/catalog'

export default {
  name: 'HostRealEstateBlacklist',

  props: {
    hostId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      confirmDialog: false,
      itemToRemove: null,
      selectedRealEstates: [],
      notes: '',
      realEstateSearch: '',
      formatDate: catalogDate,
      headers: [
        { text: this.$t('host.realEstateBlacklist.columns.date'), value: 'date_created', width: '130px' },
        { text: this.$t('host.realEstateBlacklist.columns.realEstate'), value: 'real_estate_name', width: '200px' },
        { text: this.$t('host.realEstateBlacklist.columns.blockedBy'), value: 'blocked_by_name', width: '160px' },
        { text: this.$t('host.realEstateBlacklist.columns.notes'), value: 'notes', width: '200px' },
        { text: this.$t('host.realEstateBlacklist.columns.action'), value: 'actions', width: '80px', sortable: false },
      ],
    }
  },

  computed: {
    ...mapGetters(['hostRealEstateBlacklist', 'realEstateList', 'isRealEstateSearching']),
  },

  watch: {
    realEstateSearch(val) {
      if (val && !this.selectedRealEstates.includes(val)) {
        this.fetchRealEstatesBySearchText(val)
      }
    },
  },

  created() {
    this.fetchHostRealEstateBlacklist(this.hostId)
  },

  methods: {
    ...mapActions([
      'fetchHostRealEstateBlacklist',
      'addToHostRealEstateBlacklist',
      'removeFromHostRealEstateBlacklist',
      'fetchRealEstatesBySearchText',
    ]),

    openConfirmDialog(item) {
      this.itemToRemove = item
      this.confirmDialog = true
    },

    async confirmRemove() {
      this.confirmDialog = false
      await this.removeFromHostRealEstateBlacklist({
        id: this.itemToRemove.id,
        hostId: this.hostId,
      })
      this.itemToRemove = null
    },

    async submitBlacklist() {
      const realEstateIds = this.selectedRealEstates.map(item =>
        typeof item === 'object' && item !== null ? item.id : item
      )
      const notes = this.notes

      this.selectedRealEstates = []
      this.notes = ''
      this.realEstateSearch = ''

      await this.addToHostRealEstateBlacklist({
        host_id: this.hostId,
        real_estate_ids: realEstateIds,
        notes,
      })
    },
  },
}
</script>

<style scoped>
.notes-cell {
  display: inline-block;
  max-width: 180px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}
</style>
